import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type GalleryStatus = 'published' | 'hidden';
export type GalleryLanguage = 'ru' | 'en';
export type GalleryFeature =
  | 'download'
  | 'downloadVideo'
  | 'print'
  | 'favourite'
  | 'favouriteNotes'
  | 'faceRecognition'
  | 'clientMarkPrivate'
  | 'welcomeCards'
  | 'watermark';

export interface GalleryCategoryRef {
  id: string;
  name: string;
}

export interface GalleryCategoryVocabularyEntry {
  id: string;
  name: string;
  galleryCount: number;
}

export interface GallerySettingsDTO {
  status: GalleryStatus;
  shootDate: string | null;
  expiresAt: string | null;
  urlSlug: string | null;
  // Spec 07: the per-gallery display language shown to the client.
  language: GalleryLanguage;
  downloadEnabled: boolean;
  downloadVideoEnabled: boolean;
  printCtaEnabled: boolean;
  favouritesEnabled: boolean;
  commentsEnabled: boolean;
  faceRecognitionEnabled: boolean;
  clientMarkPrivateEnabled: boolean;
  watermarkEnabled: boolean;
  // Onboarding walk-through cards for a client's first visit (spec 11 §5) — default off.
  welcomeCardsEnabled: boolean;
  // Portfolio listing (spec 04 §7.2) — read server-side from the membership row.
  isPortfolioMember: boolean;
  categories: GalleryCategoryRef[];
}

export interface ApplyCategoriesPayload {
  assign?: string[];
  remove?: string[];
  createNames?: string[];
}

@Injectable({ providedIn: 'root' })
export class GallerySettingsApiService {
  private readonly http = inject(HttpClient);

  getSettings(galleryId: string): Promise<GallerySettingsDTO> {
    return firstValueFrom(this.http.get<GallerySettingsDTO>(`/api/gallery/${galleryId}/settings`));
  }

  setStatus(galleryId: string, status: GalleryStatus): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/gallery/${galleryId}/status`, { status }));
  }

  setShootDate(galleryId: string, shootDate: string | null): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/gallery/${galleryId}/shoot-date`, { shootDate }));
  }

  setUrlSlug(galleryId: string, urlSlug: string): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/gallery/${galleryId}/url-slug`, { urlSlug }));
  }

  setFeature(galleryId: string, feature: GalleryFeature, enabled: boolean): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/gallery/${galleryId}/feature`, { feature, enabled }));
  }

  setLanguage(galleryId: string, language: GalleryLanguage): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/gallery/${galleryId}/language`, { language }));
  }

  setPortfolioMembership(galleryId: string, member: boolean): Promise<{ ok: true }> {
    return firstValueFrom(this.http.put<{ ok: true }>(`/api/portfolio/galleries/${galleryId}/membership`, { member }));
  }

  getCategoryVocabulary(): Promise<GalleryCategoryVocabularyEntry[]> {
    return firstValueFrom(this.http.get<GalleryCategoryVocabularyEntry[]>(`/api/gallery-categories`));
  }

  applyCategories(galleryId: string, payload: ApplyCategoriesPayload): Promise<{ ok: true; galleryId: string }> {
    return firstValueFrom(
      this.http.post<{ ok: true; galleryId: string }>(`/api/gallery-categories/galleries/${galleryId}/apply`, payload),
    );
  }

  // Словарь общий на аккаунт: три метода ниже правят его целиком, а не ярлык этой галереи —
  // переименование и удаление доезжают до каждой галереи с этой категорией (спека 29, 30).
  createCategory(name: string): Promise<{ ok: true }> {
    return firstValueFrom(this.http.post<{ ok: true }>(`/api/gallery-categories`, { name }));
  }

  renameCategory(id: string, name: string): Promise<{ ok: true }> {
    return firstValueFrom(this.http.patch<{ ok: true }>(`/api/gallery-categories/${id}`, { name }));
  }

  deleteCategory(id: string): Promise<{ ok: true }> {
    return firstValueFrom(this.http.delete<{ ok: true }>(`/api/gallery-categories/${id}`));
  }
}
